/**
 * The iOS app's bridge, from the page's side (ios-app-plan §5.3, §5.4).
 *
 * Inside the app the page runs in a WKWebView at `app://fusionwx/`, and the
 * app answers calls on one message handler, `fusionwx`. Nothing else in
 * `web/` touches `window.webkit`: a module that wants the app asks here, and
 * everywhere outside the app every call is a no-op that answers null.
 *
 * Every reply is checked before it is used, the way `places.svelte.ts` checks
 * a `#places=` link, because the page and the app ship together today and
 * drift apart the first time a debug build meets a release one.
 * `ios/FusionKit/Tests/FusionKitTests/bridge-contract.json` is the shared set
 * of examples; `native.test.ts` holds this file to it and the Swift tests hold
 * the app to it.
 */

/** What WebKit puts on `window` for a handler added with a reply. */
interface ReplyHandler {
  postMessage(message: unknown): Promise<unknown>
}

declare global {
  interface Window {
    webkit?: { messageHandlers?: Record<string, ReplyHandler | undefined> }
    /** Where the app delivers its events (`NativeEvent`). Set by `onNativeNavigate`. */
    __fusionwx?: { receive(message: unknown): boolean }
  }
}

function handler(): ReplyHandler | null {
  if (typeof window === 'undefined') return null
  return window.webkit?.messageHandlers?.fusionwx ?? null
}

/** The page is running inside the iOS app. */
export function isNativeShell(): boolean {
  return handler() !== null
}

/** Every call the page makes, as it is sent. */
export type NativeRequest =
  | { call: 'app.version' }
  | { call: 'theme'; choice: 'system' | 'light' | 'dark' }
  | { call: 'push.state' }
  | { call: 'push.enable' }
  | { call: 'location.current' }
  | { call: 'location.permission' }
  | { call: 'here.state' }
  | { call: 'here.alerts'; on: boolean }
  | { call: 'app.settings' }
  | { call: 'store.sync'; places: NativePlace[]; openTo: string }
  | { call: 'haptic'; kind: HapticKind }
  | { call: 'share'; path: string; title: string }

/** Send one call; null outside the app, or when the app refused it. */
async function send(message: NativeRequest): Promise<unknown> {
  const h = handler()
  if (!h) return null
  try {
    return await h.postMessage(message)
  } catch (e) {
    // A refusal is a bug on one side of the contract, never a user's doing.
    console.warn('native call failed', message.call, e)
    return null
  }
}

export interface NativeVersion {
  /** The app's marketing version, "1.0". */
  version: string
  /** The build: the `<commit count>` half of the web build id. */
  build: number
}

export function parseVersionReply(raw: unknown): NativeVersion | null {
  if (typeof raw !== 'object' || raw === null) return null
  const { version, build } = raw as Record<string, unknown>
  if (typeof version !== 'string' || version === '') return null
  if (typeof build !== 'number' || !Number.isInteger(build) || build < 0) return null
  return { version, build }
}

/** The app's version and build, for Settings → About; null outside the app. */
export async function appVersion(): Promise<NativeVersion | null> {
  if (!isNativeShell()) return null
  return parseVersionReply(await send({ call: 'app.version' }))
}

/**
 * Tell the app which theme the page chose, so the status bar and the ground
 * behind the page follow it. The *choice*, not what it resolved to: an app
 * forced to dark reports dark to the page's `prefers-color-scheme` as well,
 * so sending "dark" for System would pin the page to dark from then on.
 */
export function nativeTheme(choice: 'system' | 'light' | 'dark'): void {
  if (!isNativeShell()) return
  void send({ call: 'theme', choice })
}

/**
 * What the app says about notifications. `token` is the APNs device token in
 * hex, and `env` which APNs it belongs to: a debug build's is `sandbox` and a
 * TestFlight build's `production`, and each is refused by the other's host.
 */
export interface NativePush {
  permission: NotificationPermission
  token: string | null
  env: 'sandbox' | 'production' | null
}

const PERMISSIONS: readonly string[] = ['granted', 'denied', 'default']
/** The server's `check_token` (app/push/apns.py): hex of 16 to 200 bytes. */
const TOKEN_RE = /^(?:[0-9a-f]{2}){16,200}$/

export function parsePushReply(raw: unknown): NativePush | null {
  if (typeof raw !== 'object' || raw === null) return null
  const { permission, token, env } = raw as Record<string, unknown>
  if (typeof permission !== 'string' || !PERMISSIONS.includes(permission)) return null
  if (token === null && env === null) {
    return { permission: permission as NotificationPermission, token: null, env: null }
  }
  if (typeof token !== 'string' || !TOKEN_RE.test(token)) return null
  if (env !== 'sandbox' && env !== 'production') return null
  return { permission: permission as NotificationPermission, token, env }
}

/** The permission and the token, without asking; null outside the app. */
export async function nativePushState(): Promise<NativePush | null> {
  if (!isNativeShell()) return null
  return parsePushReply(await send({ call: 'push.state' }))
}

/**
 * Ask for permission if it has not been asked, then register with APNs.
 * **Call it straight from a tap**, before any `await`, as `enableAlerts` does
 * with the web's prompt: iOS does not need the gesture here, but the bell
 * should behave the same in both and the prompt should follow the thumb.
 */
export function nativePushEnable(): Promise<NativePush | null> {
  if (!isNativeShell()) return Promise.resolve(null)
  return send({ call: 'push.enable' }).then(parsePushReply)
}

/** Open this app's page in iOS Settings, where location and notifications are
 *  switched. A no-op outside the app. */
export function openAppSettings(): void {
  if (!isNativeShell()) return
  void send({ call: 'app.settings' })
}

/**
 * Core Location's answer for the app (ios-app-plan §5.3). `whenInUse` and
 * `always` both let the page ask for a fix; only `always` reaches a closed
 * app, which is what alerts where I am need.
 */
export type NativeLocationPermission = 'prompt' | 'whenInUse' | 'always' | 'denied'

const LOCATION_PERMISSIONS: readonly string[] = ['prompt', 'whenInUse', 'always', 'denied']

export type NativeFix = { lat: number; lon: number } | { error: 'denied' | 'unavailable' | 'timeout' }

function finiteWithin(value: unknown, bound: number): value is number {
  return typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= bound
}

export function parseFixReply(raw: unknown): NativeFix | null {
  if (typeof raw !== 'object' || raw === null) return null
  const { lat, lon, error } = raw as Record<string, unknown>
  if (error === 'denied' || error === 'unavailable' || error === 'timeout') return { error }
  if (finiteWithin(lat, 90) && finiteWithin(lon, 180)) return { lat, lon }
  return null
}

export function parseLocationPermissionReply(raw: unknown): NativeLocationPermission | null {
  if (typeof raw !== 'object' || raw === null) return null
  const { permission } = raw as Record<string, unknown>
  return typeof permission === 'string' && LOCATION_PERMISSIONS.includes(permission)
    ? (permission as NativeLocationPermission)
    : null
}

/** One fix from Core Location, asking for when-in-use permission if it has
 *  never been asked; null outside the app or on a malformed reply. */
export async function nativeLocation(): Promise<NativeFix | null> {
  if (!isNativeShell()) return null
  return parseFixReply(await send({ call: 'location.current' }))
}

/** The app's location permission, without asking; null outside the app. */
export async function nativeLocationPermission(): Promise<NativeLocationPermission | null> {
  if (!isNativeShell()) return null
  return parseLocationPermissionReply(await send({ call: 'location.permission' }))
}

/**
 * Alerts where I am (ios-app-plan §6.4): whether the switch is on, what iOS
 * allows, and when the server last took the phone's position. The app owns
 * all of it — it reports from the background with no page running — so the
 * page reads it rather than keeping a copy.
 */
export interface NativeHere {
  on: boolean
  permission: NativeLocationPermission
  /** Milliseconds since 1970, or null before the first report. */
  reported: number | null
}

export function parseHereReply(raw: unknown): NativeHere | null {
  if (typeof raw !== 'object' || raw === null) return null
  const { on, reported } = raw as Record<string, unknown>
  const permission = parseLocationPermissionReply(raw)
  if (typeof on !== 'boolean' || !permission) return null
  if (reported !== null && !(typeof reported === 'number' && Number.isInteger(reported) && reported >= 0)) {
    return null
  }
  return { on, permission, reported }
}

export async function nativeHereState(): Promise<NativeHere | null> {
  if (!isNativeShell()) return null
  return parseHereReply(await send({ call: 'here.state' }))
}

/**
 * Turn alerts where I am on or off. On asks iOS for Always, from this call
 * only (App Review looks for exactly that), starts significant-change
 * monitoring and reports the first position; off stops it and deletes the
 * server's copy.
 */
export async function nativeHereAlerts(on: boolean): Promise<NativeHere | null> {
  if (!isNativeShell()) return null
  return parseHereReply(await send({ call: 'here.alerts', on }))
}

/**
 * A saved place as the widgets see it (ios-app-plan §5.6): enough to name it
 * in the widget's place list and to ask `/api/glance` about it. Nothing else
 * of the list leaves the page.
 */
export interface NativePlace {
  id: string
  name: string
  lat: number
  lon: number
}

/**
 * Hand the saved places and the default to the app, which writes them to the
 * App Group the widgets read and reloads their timelines. The page's list
 * stays the one source of truth (plan gotcha 7): the app's copy is a cache,
 * written whole on every change and at every launch.
 */
export function nativeStoreSync(list: readonly NativePlace[], openTo: string): void {
  if (!isNativeShell()) return
  const places = list.map(({ id, name, lat, lon }) => ({ id, name, lat, lon }))
  void send({ call: 'store.sync', places, openTo })
}

/**
 * `selection` is a tick (a slider step, a tab), `impact` a tap that did
 * something (a bell), `success` a thing done (a place saved).
 */
export type HapticKind = 'selection' | 'impact' | 'success'

/** A haptic in the app; nothing on the web. Fire and forget. */
export function haptic(kind: HapticKind): void {
  if (!isNativeShell()) return
  void send({ call: 'haptic', kind })
}

/**
 * Share a link to this app through iOS's share sheet. The page's own origin in
 * the app is `app://fusionwx`, which no other device can open, so the page
 * sends a *path* and the app joins it to the server it talks to. Answers
 * whether the app took it; false outside the app.
 */
export async function nativeShare(path: string, title: string): Promise<boolean> {
  if (!isNativeShell() || !ownPath(path)) return false
  return parseShareReply(await send({ call: 'share', path, title })) === true
}

export function parseShareReply(raw: unknown): boolean | null {
  if (typeof raw !== 'object' || raw === null) return null
  const { shared } = raw as Record<string, unknown>
  return typeof shared === 'boolean' ? shared : null
}

/** A path on this page: one leading slash, and never `//` or `/\`, which a
 *  URL parser would read as another host. */
function ownPath(path: unknown): path is string {
  return typeof path === 'string' && /^\/(?![/\\])/.test(path)
}

/** An event from the app, checked; null for anything the contract refuses. */
export function parseNativeEvent(raw: unknown): { event: 'navigate'; path: string } | null {
  if (typeof raw !== 'object' || raw === null) return null
  const { event, path } = raw as Record<string, unknown>
  if (event !== 'navigate' || !ownPath(path)) return null
  return { event, path }
}

const navigateListeners = new Set<(path: string) => void>()

/**
 * A notification tapped while the page is open: the app hands over its path
 * here rather than reloading the page, as the service worker posts
 * `fusionwx:open` in the PWA. `receive` answers whether anybody took it; the
 * app loads the path as a fresh page when nobody did. Returns a disposer.
 */
export function onNativeNavigate(listener: (path: string) => void): () => void {
  if (!isNativeShell()) return () => {}
  window.__fusionwx ??= {
    receive(message: unknown): boolean {
      const parsed = parseNativeEvent(message)
      if (!parsed) {
        console.warn('ignoring a malformed event from the app')
        return false
      }
      for (const l of navigateListeners) l(parsed.path)
      return navigateListeners.size > 0
    },
  }
  navigateListeners.add(listener)
  return () => navigateListeners.delete(listener)
}
